/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthModalStore } from '@/stores/auth-modal-store'
import { Button } from '@/components/ui/button'
import { AnimateInView } from '@/components/animate-in-view'

interface CTAProps {
  className?: string
  isAuthenticated?: boolean
}

export function CTA(props: CTAProps) {
  const { openSignUp, openSignIn } = useAuthModalStore()
  const { t } = useTranslation()

  return (
    <section className='relative z-10 overflow-hidden px-6 py-12 md:py-16'>
      {/* Solid burgundy background */}
      <div
        aria-hidden
        className='absolute inset-0 -z-10 bg-red-800'
      />

      <AnimateInView
        className='mx-auto max-w-2xl text-center'
        animation='scale-in'
      >
        <h2 className='text-2xl leading-tight font-bold tracking-tight text-white md:text-4xl'>
          {t('Ready to simplify')}
          <br />
          <span className='text-white/90'>
            {t('your AI integration?')}
          </span>
        </h2>
        <p className='text-white/70 mx-auto mt-5 max-w-xl text-sm leading-relaxed md:text-base'>
          {t(
            'Free registration with 100,000 Token trial quota, experience 8 major providers and 40+ models unified access immediately'
          )}
        </p>
        <div className='mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center'>
          {props.isAuthenticated ? (
            <div className='rounded-xl bg-white/20 px-8 py-6 text-base font-semibold text-white backdrop-blur-sm'>
              {t('You are already registered and logged in, no need to register again.')}
            </div>
          ) : (
            <Button
              className='group w-full sm:w-auto rounded-xl bg-white px-8 py-6 text-base font-semibold text-red-600 shadow-lg shadow-red-600/20 hover:bg-white/95 hover:shadow-xl'
              onClick={openSignUp}
            >
              {t('Start Register')}
              <ArrowRight className='ml-2 size-4 transition-transform duration-200 group-hover:translate-x-1' />
            </Button>
          )}
          {props.isAuthenticated ? (
            <Button
              variant='outline'
              className='w-full sm:w-auto rounded-xl border-2 border-white/50 bg-white/10 px-8 py-6 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:border-white'
              render={<Link to='/pricing' />}
            >
              {t('View Pricing')}
            </Button>
          ) : (
            <Button
              variant='outline'
              className='w-full sm:w-auto rounded-xl border-2 border-white/50 bg-white/10 px-8 py-6 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:border-white'
              onClick={openSignIn}
            >
              {t('View Pricing')}
            </Button>
          )}
        </div>
      </AnimateInView>
    </section>
  )
}
